import { useRef, useState } from 'react';
import { api } from '../api.ts';
import { useApp } from '../store.tsx';
import { Card, Sheet } from '../components.tsx';
import { formatDate, formatMoney } from '../format.ts';

interface PreviewRow {
  rowIndex: number;
  ok: boolean;
  error?: string;
  raw?: string[];
  transaction?: {
    amountMinor: number; currency: string; transactionType: string;
    merchantDisplay: string; suggestedCategory: string | null; occurredAt: string;
  };
  isDuplicate?: boolean;
  duplicateReason?: string | null;
}

interface PreviewResponse {
  ok: boolean;
  reason?: string;
  message?: string;
  previewId?: string;
  mapping: { index: number; header: string; role: string; confidence: number }[];
  summary?: { totalRows: number; importable: number; duplicates: number; failed: number };
  rows?: PreviewRow[];
  truncated?: boolean;
}

/**
 * CSV import. Preview is mandatory — the user sees exactly which rows would be
 * created, which are duplicates, and which could not be read, before anything
 * touches their ledger.
 */
export function ImportScreen({ onClose, onImported }: { onClose: () => void; onImported: () => void }): JSX.Element {
  const { t, locale, toast } = useApp();
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [includeDuplicates, setIncludeDuplicates] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const onFile = async (file: File) => {
    setBusy(true);
    setError(null);
    setPreview(null);
    try {
      const content = await file.text();
      const res = await api.post<PreviewResponse>('/import/csv/preview', { content });
      setPreview(res);
      if (!res.ok) setError(res.message ?? t('somethingWrong'));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!preview?.previewId) return;
    setBusy(true);
    try {
      const res = await api.post<{ imported: number; skipped: number }>('/import/csv/commit', {
        previewId: preview.previewId,
        includeDuplicates,
      });
      toast(`${t('importedCount')} ${res.imported}`);
      onImported();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title={t('csvImport')} onClose={onClose}>
      <div className="stack">
        {!preview && (
          <>
            <p className="small muted" style={{ lineHeight: 1.6 }}>{t('csvHelp')}</p>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv,text/plain"
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }}
            />
            <button className="btn btn--primary btn--block" disabled={busy} onClick={() => fileRef.current?.click()}>
              {busy ? t('loading') : t('chooseFile')}
            </button>
          </>
        )}

        {error && <div className="field__error">{error}</div>}

        {preview?.ok && preview.summary && (
          <>
            <Card>
              <div className="row" style={{ gap: 14, justifyContent: 'space-around', textAlign: 'center' }}>
                <Stat value={preview.summary.importable} label={t('importable')} color="var(--positive)" />
                <Stat value={preview.summary.duplicates} label={t('duplicatesFound')} color="var(--warning)" />
                <Stat value={preview.summary.failed} label={t('failedRows')} color="var(--text-tertiary)" />
              </div>
            </Card>

            <details>
              <summary className="small muted" style={{ cursor: 'pointer', padding: '6px 0' }}>
                {t('detectedColumns')}
              </summary>
              <Card style={{ marginTop: 6 }}>
                {preview.mapping.map((m) => (
                  <div key={m.index} className="row row--between" style={{ paddingBlock: 5 }}>
                    <span className="tiny truncate grow">{m.header || `#${m.index + 1}`}</span>
                    <span className="tiny strong" style={{
                      color: m.role === 'ignore' ? 'var(--text-tertiary)' : 'var(--accent)',
                    }}>{m.role}</span>
                  </div>
                ))}
              </Card>
            </details>

            {preview.summary.duplicates > 0 && (
              <label className="row" style={{ gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={includeDuplicates}
                       onChange={(e) => setIncludeDuplicates(e.target.checked)}
                       style={{ width: 18, height: 18, accentColor: 'var(--accent)' }} />
                <span className="small">
                  {locale === 'ar' ? 'استورد المكررات أيضاً' : 'Import duplicates too'}
                </span>
              </label>
            )}

            <div className="section-title">{locale === 'ar' ? 'معاينة' : 'Preview'}</div>
            <Card className="card--flush" style={{ maxHeight: 320, overflowY: 'auto' }}>
              {preview.rows?.slice(0, 60).map((r) => (
                <div key={r.rowIndex} className="row row--between"
                     style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', gap: 10, opacity: r.ok ? 1 : 0.55 }}>
                  <span className="grow" style={{ minWidth: 0 }}>
                    <span className="small truncate" style={{ display: 'block' }}>
                      {r.ok ? r.transaction!.merchantDisplay || '—' : (r.raw?.join(' · ').slice(0, 40) ?? '—')}
                    </span>
                    <span className="tiny muted">
                      {r.ok
                        ? formatDate(r.transaction!.occurredAt, locale)
                        : r.error === 'no_date'
                          ? (locale === 'ar' ? 'تاريخ غير صالح' : 'Unreadable date')
                          : (locale === 'ar' ? 'مبلغ مفقود' : 'Missing amount')}
                      {r.isDuplicate && ` · ${t('duplicatesFound')}`}
                    </span>
                  </span>
                  {r.ok && (
                    <span className="small money" style={{
                      color: r.isDuplicate ? 'var(--text-tertiary)' : undefined,
                      textDecoration: r.isDuplicate && !includeDuplicates ? 'line-through' : undefined,
                    }}>
                      {/* Same exception as the paste preview: this is the file
                          the user just picked, shown so they can confirm it
                          parsed correctly before any of it is saved. */}
                      {formatMoney(r.transaction!.amountMinor, r.transaction!.currency, { locale })}
                    </span>
                  )}
                </div>
              ))}
            </Card>

            <p className="tiny muted center">{t('previewNote')}</p>

            <div className="row" style={{ gap: 10 }}>
              <button className="btn btn--secondary grow" onClick={() => { setPreview(null); setError(null); }}>
                {t('cancel')}
              </button>
              <button className="btn btn--primary grow" disabled={busy} onClick={commit}>
                {busy ? t('importing') : t('importNow')}
              </button>
            </div>
          </>
        )}
      </div>
    </Sheet>
  );
}

function Stat({ value, label, color }: { value: number; label: string; color: string }): JSX.Element {
  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 690, color, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      <div className="tiny muted">{label}</div>
    </div>
  );
}
